import { Ref } from 'vue';

import { loadTokenEntry, persistVisibleEstimate } from './cookieStorage';

export interface PositionCountdownTypes {
    ch: Ref<{ position: number | null }>;
    token: string;                    // e.g., "10yearsofLunaLapin_token" (key for cookie)
    position: number | null;          // server "position" (can be null)
    result_position?: number | null;  // if server provides a target end-of-ttl position
    ratePerMinute: number | null;     // server "rate" (sessions/min)
    ttl?: number;                     // seconds until the next poll (default 60)
}

let positionTimer: number | undefined;
let storageName: string = '';
let lastPersistTime = 0;
let lastDecrementTs = 0;
const PERSIST_INTERVAL = 1000; // Throttle cookie writes to once per second

const clearPositionCountdownInterval = (): void => {
    if (positionTimer !== undefined) {
        clearTimeout(positionTimer);
        positionTimer = undefined;
    }
};

/**
 * Compute ms-per-step and step size so we move from `startPos` to `targetPos`
 * across exactly `ttlSeconds`. When the delta is large, step size increases
 * beyond 1 so we can reach the target at 120ms ticks at most.
 *
 * Critically: stepMs is derived from the *actual* tick count after stepSize
 * is chosen, so total animation duration always matches ttl regardless of
 * rounding. (Previously, `ceil(delta / maxTicks)` could produce a stepSize
 * that finished the countdown well before ttl when delta wasn't a clean
 * multiple of maxTicks.)
 *
 * Returns { stepMs, stepSize } or null if no movement needed.
 */
function computeStep(ttlSeconds: number, startPos: number, targetPos: number): { stepMs: number; stepSize: number } | null {
    const delta = Math.max(0, startPos - targetPos);
    if (delta <= 0 || ttlSeconds <= 0) return null;

    const MIN_STEP_MS = 120;
    const totalMs = ttlSeconds * 1000;

    // Maximum ticks we can fit at the minimum safe interval.
    const maxTicks = Math.floor(totalMs / MIN_STEP_MS);

    if (maxTicks <= 0) return null;

    // Step size: 1 for small deltas (decrement one at a time), otherwise
    // large enough to finish within maxTicks ticks.
    const stepSize = delta <= maxTicks ? 1 : Math.ceil(delta / maxTicks);

    // Derive stepMs from the *actual* number of ticks we'll fire, so
    // the total animation duration matches ttl. Without this, a rounded-up
    // stepSize would cause the countdown to finish early.
    const ticksNeeded = Math.ceil(delta / stepSize);
    const stepMs = Math.floor(totalMs / ticksNeeded);

    return { stepMs, stepSize };
}


/**
 * Decrement ch.value.position by stepSize every stepMs until it reaches targetPos.
 *
 * On poll transitions the first tick is scheduled relative to when the
 * last decrement happened, maintaining a consistent visual rhythm even
 * when the step rate changes between cycles.
 */
function startTimeBasedCountdown(
    ch: Ref<{ position: number | null }>,
    targetPos: number,
    stepMs: number,
    stepSize: number = 1
) {
    clearPositionCountdownInterval();

    // Schedule first tick to maintain rhythm from the previous cycle.
    // On firstrun lastDecrementTs is 0, so firstDelay falls back to stepMs.
    const timeSinceLast = lastDecrementTs ? Date.now() - lastDecrementTs : 0;
    const firstDelay = Math.max(0, stepMs - timeSinceLast);

    const tick = () => {
        if (ch.value.position !== null && ch.value.position > targetPos) {
            ch.value.position = Math.max(targetPos, ch.value.position - stepSize);
            lastDecrementTs = Date.now();

            if (storageName && !document.hidden && lastDecrementTs - lastPersistTime >= PERSIST_INTERVAL) {
                persistVisibleEstimate(storageName, ch.value.position);
                lastPersistTime = lastDecrementTs;
            }

            if (ch.value.position > targetPos) {
                positionTimer = window.setTimeout(tick, stepMs);
            } else {
                positionTimer = undefined;
            }
        } else {
            positionTimer = undefined;
        }
    };

    positionTimer = window.setTimeout(tick, firstDelay);
}

let firstrun: boolean = true;

/**
 * Reset the firstrun flag. Call this on component unmount to ensure
 * proper initialization on remount.
 */
const resetFirstRun = (): void => {
    firstrun = true;
    lastPersistTime = 0;
    lastDecrementTs = 0;
};

const applyPoll = (params: PositionCountdownTypes) => {


    const { ch, token, ttl = 60 } = params;

    storageName = token;

    // Checking-in / shuffling phase: the server hasn't assigned a position yet,
    // so there's nothing to count down. This is a normal, expected state —
    // return quietly rather than warning (the warning here was a red herring
    // that masked a template bug during the countdown→active transition).
    if (params.position == null) {
        return;
    }

    // Position is present but the data needed to animate it isn't — this IS
    // unexpected, so warn with the specific missing field.
    if (params.result_position == null || params.ratePerMinute == null) {
        console.warn(`applyPoll skipped: missing ${params.result_position == null ? 'result_position' : 'ratePerMinute'}`);
        return;
    }

    const serverTarget = params.result_position ?? null;
    const ratePerMinute = params.ratePerMinute ?? null;
    const targetPos = serverTarget;
    const cookieValues = loadTokenEntry(token);
    let startPos = params.position;

    if (cookieValues && cookieValues.mostRecentPosition && firstrun) {
        startPos = Math.max(0, Math.floor(cookieValues.mostRecentPosition));
    } else if (firstrun) {
        startPos = Math.max(0, Math.floor(targetPos + ratePerMinute));
    }

    // On firstrun, set the initial display position
    if (firstrun) {
        ch.value.position = startPos;
        firstrun = false;
    }

    if (targetPos < ch.value.position!) {
        const step = computeStep(ttl, ch.value.position!, targetPos);
        if (step) startTimeBasedCountdown(ch, targetPos, step.stepMs, step.stepSize);
    } else {
        ch.value.position = targetPos;
        clearPositionCountdownInterval();
    }

}

/**
 * Count position down to 0 then fire a callback.
 * Used when the user is promoted — gives visual feedback before redirect.
 * Position > 20: compress entire countdown into ~5 seconds.
 * Position ≤ 20: 250ms per step.
 */
function countdownToZero(
    ch: Ref<{ position: number | null }>,
    onComplete: () => void
): void {
    clearPositionCountdownInterval();

    const pos = ch.value.position;
    if (pos === null || pos <= 0) {
        onComplete();
        return;
    }

    const stepMs = pos > 20 ? Math.floor(5000 / pos) : 250;

    const tick = () => {
        if (ch.value.position !== null && ch.value.position > 1) {
            ch.value.position--;
            if (ch.value.position > 1) {
                positionTimer = window.setTimeout(tick, stepMs);
            } else {
                positionTimer = undefined;
                onComplete();
            }
        } else {
            positionTimer = undefined;
            onComplete();
        }
    };

    positionTimer = window.setTimeout(tick, stepMs);
}

export { clearPositionCountdownInterval, applyPoll, resetFirstRun, countdownToZero };
